// src/features/admin/components/SystemManagement.tsx
import React, { useEffect, useMemo, useState } from 'react';
import Tabs from '@/shared/components/ui/Tabs';
import { useAuth } from '@/shared/auth';
import { useQuery } from '@tanstack/react-query';
import TableSuperadminRooms, { type Room } from '@/features/services/ttlock/components/table_superadmin_rooms';
import TableAdminRooms from '@/features/services/ttlock/components/table_admin_rooms';
import { listLocks, type Lock } from '@/features/services/ttlock/api/locks';
import RoomCreateDialog from '@/features/services/ttlock/components/AppDialog_CreateRoom';

// Only one subtab (Rooms) now, but structured for future expansion
type TabKey = 'rooms';
const TAB_LABELS: Record<TabKey, string> = { rooms: 'Rooms' };

export default function SystemManagement() {
  const auth = useAuth();
  const { perms, systemTabs } = auth;
  const permList = useMemo(() => Array.from(perms.values()), [perms]);
  const has = (p: string) => permList.includes(p);

  const canSuper = has('ttlock.room.superadmin');
  const canAdmin = has('ttlock.room.admin') || canSuper; // super includes admin

  // Build subtabs list (only show Rooms if at least admin)
  const subtabs = useMemo(() => {
    const fromServer = (systemTabs || []).filter(t => t && t.key === 'rooms');
    if (fromServer.length && canAdmin) {
      return fromServer.map(t => ({ key: 'rooms' as TabKey, label: t.label || TAB_LABELS.rooms }));
    }
    // fallback if server didn't send yet but perms allow
    if (canAdmin) return [{ key: 'rooms', label: TAB_LABELS.rooms }];
    return [];
  }, [systemTabs, canAdmin]);

  const [active, setActive] = useState<TabKey | null>((subtabs[0]?.key as TabKey) ?? null);
  useEffect(() => {
  if (!active || !subtabs.find(t => t.key === active)) setActive((subtabs[0]?.key as TabKey) ?? null);
  }, [subtabs, active]);

  // Data loading (only if rooms visible)
  const {
    data: rooms = [],
    isLoading: roomsLoading,
    error: roomsError,
    refetch: refetchRooms,
  } = useQuery<Room[]>({
    queryKey : ['ttlock','rooms','sys-mgmt'],
    queryFn  : async () => {
      const res = await fetch('/bff/ttlock/rooms', { method: 'GET', headers: { accept: 'application/json' }, credentials: 'include' });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `GET /bff/ttlock/rooms → ${res.status}`);
      }
      return res.json();
    },
    enabled: canAdmin,
    staleTime: 60_000,
  });

  const { data: locks = [], isLoading: locksLoading, error: locksError } = useQuery<Lock[]>({
    queryKey : ['ttlock','locks','sys-mgmt',1,200],
    queryFn  : () => listLocks(1,200),
    staleTime: 5*60_000,
    enabled  : canSuper, // only needed for superadmin table + create dialog
  });

  const [createOpen, setCreateOpen] = useState(false);

  if (!subtabs.length) {
    return (
      <section className="bg-neutral-900 rounded-lg p-4">
        <p className="text-neutral-400 text-sm">No system-level panels are currently available.</p>
      </section>
    );
  }

  return (
    <section className="bg-neutral-900 rounded-lg p-4 space-y-6">
      <Tabs
        tabs={subtabs.map(t => t.label)}
        activeTab={subtabs.find(t => t.key === active)?.label ?? subtabs[0].label}
        onTabChange={(label) => {
          const found = subtabs.find(t => t.label === label); if (found) setActive(found.key as TabKey);
        }}
      />

      {active === 'rooms' && (
        <div className="space-y-6">
          <header className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Rooms</h2>
            {canSuper && (
              <button
                onClick={() => setCreateOpen(true)}
                className="px-3 py-1 text-sm rounded bg-neutral-800 hover:bg-neutral-700 text-neutral-200"
              >
                New Room
              </button>
            )}
          </header>

          {canSuper && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-neutral-300">Superadmin Rooms</h3>
              <TableSuperadminRooms
                rooms={rooms}
                locks={locks}
                loading={roomsLoading || locksLoading}
                error={roomsError || locksError}
                onRefresh={() => void refetchRooms()}
                onNew={() => setCreateOpen(true)}
              />
            </div>
          )}

          {canAdmin && !canSuper && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-neutral-300">Admin Rooms</h3>
              <TableAdminRooms
                rooms={rooms}
                loading={roomsLoading}
                error={roomsError}
                onRefresh={() => void refetchRooms()}
              />
            </div>
          )}

          {canSuper && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-neutral-300">Admin Rooms</h3>
              <TableAdminRooms
                rooms={rooms}
                loading={roomsLoading}
                error={roomsError}
                onRefresh={() => void refetchRooms()}
              />
            </div>
          )}

          {canSuper && (
            <RoomCreateDialog
              open={createOpen}
              onOpenChange={setCreateOpen}
              locks={locks}
              locksLoading={locksLoading}
              locksError={locksError}
              onCreated={async () => { await refetchRooms(); }}
            />
          )}
        </div>
      )}
    </section>
  );
}
